import * as zod from 'zod';
import SectionHeader from "../CoreComponents/Details/SectionHeader";
import { fetchApi, splitOnCapitals } from '../../Helpers/Functions';
import FormInput from '../CoreComponents/ReactFormComponents/FormInput';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalOpen } from '../../store/Utilities';
import { useDispatch } from 'react-redux';
import { URLs } from '../../Constants/Consts';
import { toast } from 'react-toastify';
import { addSingleInspection } from '../../store/Inspections';
import { addModel } from '../../store/Model';

type TProps = {
    handleRefetchModels?: () => void;
}

const schema = zod.object({
    modelNumber: zod.string().min(1, { message: "Please enter the model number" }),
    modelDescription: zod.string().optional(),
});

type ModelFormType = zod.infer<typeof schema>;

export function AddModelForm({ handleRefetchModels }: TProps) {
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
    } = useForm<ModelFormType>({
        resolver: zodResolver(schema),
        defaultValues: {
            modelNumber: "",
            modelDescription: ""
        }
    });

    const handleCloseModal = () => {
        dispatch(setModalOpen(false));
        reset();
    };

    const onSubmit = async (data: ModelFormType) => {
        const modelData = {
            modelNumber: data.modelNumber,
            modelDescription: data.modelDescription,
            drilldownTypeId: 0,
            r3DrilldownTypeId: 0,
            s4DrilldownTypeId: 0
        };

        try {
            const response = await fetchApi(URLs.getModels(), {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(modelData)
            });

            const addModelResponse = await response.json();

            if (!response.ok) {
                throw new Error(addModelResponse?.detail || "Failed to add model");
            }

            // Add to Redux store for immediate UI update
            if (addModelResponse) {
                dispatch(addSingleInspection({
                    inspectionId: addModelResponse?.inspectionId || Date.now(),
                    disciplineId: addModelResponse?.disciplineId || 0,
                    modelNumber: data?.modelNumber,
                    inspectionDescription: data?.modelDescription,
                    inspectionVersion: "1.0"
                }));
                dispatch(addModel({
                    modelNumber: data.modelNumber,
                    modelDescription: data.modelDescription || '',
                    drilldownTypeId: addModelResponse.drilldownTypeId || 0,
                    inspectionId: addModelResponse.inspectionId,
                    inspectionIds: addModelResponse.inspectionIds || [],
                    inspectionTasks: addModelResponse.inspectionTasks || [],
                }));
            }
            dispatch(setModalOpen(false));
            handleRefetchModels?.();
            toast.success(addModelResponse?.message || "Model added successfully");
            reset();
        } catch (error) {
            console.error("Error adding model:", error);
            toast.error(error instanceof Error ? error.message : "An error occurred while adding the model.");
        }
    };

    return (
        <div className='max-w-screen-md mt-4'>
            <div className="mt-8 mb-8">
                <SectionHeader title="Model Details" />
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <FormInput
                    dataTestId="model-number-input"
                    required={true}
                    errors={errors}
                    defaultValue=""
                    inputKey="modelNumber"
                    registeredProps={register('modelNumber')}
                    label={splitOnCapitals("ModelNumber")}
                    placeholder="Model Number"
                />
                <FormInput
                    dataTestId="model-description-input"
                    errors={errors}
                    defaultValue=""
                    inputKey="modelDescription"
                    registeredProps={register('modelDescription')}
                    label={splitOnCapitals("ModelDescription")}
                    placeholder="Model Description"
                />
                <div className='flex justify-between mt-16'>
                    <button
                        type="button"
                        data-testid="add-model-form-cancel-button"
                        onClick={handleCloseModal}
                        className='mt-3 text-gray-900 bg-gray-500 dark:text-gray-900 dark:bg-gray-500'
                    >
                        Cancel
                    </button>
                    <button
                        disabled={isSubmitting}
                        type="submit"
                        data-testid="add-model-form-submit-button"
                        className='text-white bg-orange-1000 dark:text-gray-800 dark:bg-orange-1000'
                    >
                        Submit
                    </button>
                </div>
            </form>
        </div>
    );
}
